import React, { useState, useEffect } from 'react';
import { tenantsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';
import { ClipboardList, Plus, Trash2, ArrowUp, ArrowDown, Save } from 'lucide-react';
import { toast } from 'sonner';

const FILE_TYPE_OPTIONS = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx'];

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);

const emptyType = (idx = 0) => ({
  key: '',
  label: '',
  description: '',
  required: true,
  accepted_file_types: ['pdf', 'jpg', 'jpeg', 'png'],
  order: idx,
});

const RequiredDocumentTypesManager = ({ tenantId, enabled = true }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!tenantId) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await tenantsAPI.getDocumentTypes(tenantId);
        if (!cancelled) setTypes(res.data || []);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load document types');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    setLoading(true);
    load();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const addType = () => {
    setTypes((prev) => [...prev, emptyType(prev.length)]);
  };

  const removeType = (idx) => {
    setTypes((prev) => prev.filter((_, i) => i !== idx).map((t, i) => ({ ...t, order: i })));
  };

  const moveType = (idx, dir) => {
    setTypes((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next.map((t, i) => ({ ...t, order: i }));
    });
  };

  const updateField = (idx, field, value) => {
    setTypes((prev) =>
      prev.map((t, i) => {
        if (i !== idx) return t;
        const updated = { ...t, [field]: value };
        // Auto-derive key from label if key is empty or matches old slug
        if (field === 'label' && (!t.key || t.key === slugify(t.label))) {
          updated.key = slugify(value);
        }
        return updated;
      })
    );
  };

  const toggleFileType = (idx, ft) => {
    setTypes((prev) =>
      prev.map((t, i) => {
        if (i !== idx) return t;
        const set = new Set(t.accepted_file_types || []);
        if (set.has(ft)) set.delete(ft);
        else set.add(ft);
        return { ...t, accepted_file_types: Array.from(set) };
      })
    );
  };

  const handleSave = async () => {
    // Client-side validation
    const keys = new Set();
    for (const [i, t] of types.entries()) {
      const k = slugify(t.key || t.label);
      if (!k) {
        toast.error(`Row ${i + 1}: key or label is required`);
        return;
      }
      if (!t.label?.trim()) {
        toast.error(`Row ${i + 1}: label is required`);
        return;
      }
      if (keys.has(k)) {
        toast.error(`Row ${i + 1}: duplicate key "${k}"`);
        return;
      }
      keys.add(k);
    }
    const payload = types.map((t, i) => ({
      key: slugify(t.key || t.label),
      label: t.label.trim(),
      description: (t.description || '').trim() || null,
      required: !!t.required,
      accepted_file_types: t.accepted_file_types?.length ? t.accepted_file_types : ['pdf'],
      order: i,
    }));

    setSaving(true);
    try {
      const res = await tenantsAPI.updateDocumentTypes(tenantId, payload);
      setTypes(res.data.required_document_types || []);
      toast.success('Required document types saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save document types');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="required-doc-types-manager">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-indigo-600" />
            <CardTitle>Required Document Types</CardTitle>
          </div>
          <Button size="sm" variant="outline" onClick={addType} data-testid="add-doc-type-btn">
            <Plus className="mr-2 h-4 w-4" /> Add Type
          </Button>
        </div>
        <CardDescription>
          {enabled
            ? 'Define what documents users must upload. They will see this checklist on the Documents page.'
            : 'Enable the "Document Submission" module to use this checklist.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : types.length === 0 ? (
          <div className="text-center py-10 border border-dashed border-slate-200 rounded-lg">
            <ClipboardList className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No document types defined yet</p>
            <Button size="sm" className="mt-3" onClick={addType} data-testid="add-first-doc-type-btn">
              <Plus className="mr-2 h-4 w-4" /> Add first type
            </Button>
          </div>
        ) : (
          types.map((t, idx) => (
            <div
              key={idx}
              className="border border-slate-200 rounded-lg p-4 space-y-3"
              data-testid={`doc-type-row-${idx}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-mono text-slate-400">#{idx + 1}</span>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => moveType(idx, -1)}
                    disabled={idx === 0}
                    data-testid={`doc-type-up-${idx}`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => moveType(idx, 1)}
                    disabled={idx === types.length - 1}
                    data-testid={`doc-type-down-${idx}`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-red-600"
                    onClick={() => removeType(idx)}
                    data-testid={`doc-type-remove-${idx}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Label *</Label>
                  <Input
                    value={t.label}
                    onChange={(e) => updateField(idx, 'label', e.target.value)}
                    placeholder="e.g., Passport"
                    data-testid={`doc-type-label-${idx}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Key (slug)</Label>
                  <Input
                    value={t.key}
                    onChange={(e) => updateField(idx, 'key', slugify(e.target.value))}
                    placeholder="auto from label"
                    className="font-mono text-xs"
                    data-testid={`doc-type-key-${idx}`}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={t.description || ''}
                  onChange={(e) => updateField(idx, 'description', e.target.value)}
                  placeholder="What should the user upload here?"
                  rows={2}
                  data-testid={`doc-type-desc-${idx}`}
                />
              </div>

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={!!t.required}
                    onCheckedChange={(v) => updateField(idx, 'required', v)}
                    data-testid={`doc-type-required-${idx}`}
                  />
                  <Label className="text-xs">Required</Label>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {FILE_TYPE_OPTIONS.map((ft) => {
                    const active = (t.accepted_file_types || []).includes(ft);
                    return (
                      <button
                        key={ft}
                        type="button"
                        onClick={() => toggleFileType(idx, ft)}
                        className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                          active
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`doc-type-ext-${idx}-${ft}`}
                      >
                        .{ft}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ))
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving} data-testid="save-doc-types-btn">
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving…' : 'Save Document Types'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RequiredDocumentTypesManager;
